// #357 change 6 — Add Credits modal advice. Pure (no React / store) so it is unit-tested in isolation.
// `perDay` is the WORKSPACE pace (computeDailyPace over the workspace-aggregated usage series): credits are
// one pool per workspace and the owner tops up for the team, so every number here is the team's, never the
// viewer's own. No claim is made about top-up validity/expiry (op decision B: confirmed in code that purchased
// top-ups do not expire, but the UI says nothing until the op rules on wording).
import { UNLIMITED_CREDITS_MONTHLY_PRICE } from './unlimited-credits-addon';

/** The 5,000 credits / month add-on is shown to heavy burners only: when a month of top-ups at the best rate costs this share of it. */
export const UNLIMITED_HEAVY_SHARE = 0.6;

export interface AdvicePack { credits: number; price: number; perCredit: number; id: string }

/** Whole days a pack lasts at the pace (>=0). null when there is no pace signal. */
export function packLastsDays(credits: number, perDay: number | null): number | null {
  if (perDay === null || !(perDay > 0) || !(credits > 0)) return null;
  return Math.floor(credits / perDay);
}

/**
 * The pack to pre-select: the smallest one that covers the shortfall until renewal
 * (perDay * daysToRenewal - creditsRemaining); the largest when none does; null with no pace
 * (the caller then keeps its existing default).
 */
export function recommendPackCredits(args: {
  packCredits: number[];
  perDay: number | null;
  creditsRemaining: number;
  daysToRenewal: number;
}): number | null {
  const { packCredits, perDay, creditsRemaining, daysToRenewal } = args;
  if (perDay === null || !(perDay > 0) || packCredits.length === 0) return null;
  const sorted = [...packCredits].sort((a, b) => a - b);
  const horizon = daysToRenewal > 0 ? daysToRenewal : 30;
  const need = Math.max(0, perDay * horizon - Math.max(0, creditsRemaining));
  return sorted.find((c) => c >= need) ?? sorted[sorted.length - 1];
}

/** What a month at the current pace costs if bought entirely as top-ups at the cheapest per-credit rate. */
export function monthlyTopUpCost(perDay: number | null, cheapestPerCredit: number): number | null {
  if (perDay === null || !(perDay > 0) || !(cheapestPerCredit > 0)) return null;
  return perDay * 30 * cheapestPerCredit;
}

export function isHeavyBurner(monthlyCost: number | null, unlimitedPrice = UNLIMITED_CREDITS_MONTHLY_PRICE): boolean {
  return monthlyCost !== null && monthlyCost >= unlimitedPrice * UNLIMITED_HEAVY_SHARE;
}

export interface UpgradeOption { planName: string; extraCredits: number; extraPrice: number; perExtraCredit: number }

/**
 * Cheapest next plan up with more credits than the current one, priced on the incremental
 * credits (the plan also carries other features, so the honest unit is "per extra credit").
 * null when the current plan is unknown or already the top.
 */
export function nextPlanUpgrade(args: {
  currentCredits: number | null;
  currentPrice: number | null;
  plans: Array<{ name: string; credits: number; price: number }>;
}): UpgradeOption | null {
  const { currentCredits, currentPrice, plans } = args;
  if (currentCredits === null || currentPrice === null || !(currentPrice > 0)) return null;
  const next = plans
    .filter((p) => p.credits > currentCredits && p.price > currentPrice)
    .sort((a, b) => a.credits - b.credits)[0];
  if (!next) return null;
  const extraCredits = next.credits - currentCredits;
  const extraPrice = next.price - currentPrice;
  return { planName: next.name, extraCredits, extraPrice, perExtraCredit: extraPrice / extraCredits };
}
